/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計シートを「チャンク」に束ねる土台。1 枚のシートを包んで名前を付け、束ねたものを一覧・追加・改名・削除・書き換えする純関数で持つ
 * @graph-connects none
 */
import {
  createInitialChildTicketRows,
  type BasicDesignPeople,
  type ChildTicketRow,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';

/** 束ねた基本設計シート 1 つぶん。 */
export interface ChildTicketChunk {
  /** 一意キー（React の key と、今開いているチャンクの同定にだけ使う）。 */
  chunkId: string;
  /** チャンク名。既定は案件名から組むが、あとから書き換えられる。 */
  title: string;
  /** そのチャンクが持つ子票行。 */
  rows: ChildTicketRow[];
}

/** チャンク id の連番。描画のたびに変わらないよう module scope に置く。 @graph-connects none */
let chunkSeq = 0;

/**
 * 新しいチャンクの既定名。`案件名_chunk 1_new chunk` の形で、どの案件を何番目に
 * 切り出したものかが名前だけで分かるようにする。
 * @graph-connects none
 */
export function newChildTicketChunkTitle(caseTitle: string, order: number): string {
  return `${caseTitle}_chunk ${String(order)}_new chunk`;
}

/**
 * チャンクを 1 つ作る。行を渡さなければ初期セットから始める。
 * @graph-connects newChildTicketChunkTitle [calls] 既定のチャンク名
 * @graph-connects createInitialChildTicketRows [calls] 新しいチャンクの初期セット
 */
export function createChildTicketChunk(
  caseTitle: string,
  order: number,
  rows?: readonly ChildTicketRow[],
  people?: BasicDesignPeople,
): ChildTicketChunk {
  chunkSeq += 1;
  return {
    chunkId: `child-ticket-chunk-${String(chunkSeq)}`,
    title: newChildTicketChunkTitle(caseTitle, order),
    rows: rows === undefined ? createInitialChildTicketRows(people) : [...rows],
  };
}

/**
 * 末尾にチャンクを足す。
 * @graph-connects none
 */
export function addChildTicketChunk(
  chunks: readonly ChildTicketChunk[],
  chunk: ChildTicketChunk,
): ChildTicketChunk[] {
  return [...chunks, chunk];
}

/**
 * チャンクを外す。
 * @graph-connects none
 */
export function removeChildTicketChunk(
  chunks: readonly ChildTicketChunk[],
  chunkId: string,
): ChildTicketChunk[] {
  return chunks.filter((chunk) => chunk.chunkId !== chunkId);
}

/**
 * チャンク名を書き換える。空にはしない（名前の無いチャンクは一覧で見分けが付かない）。
 * @graph-connects none
 */
export function renameChildTicketChunk(
  chunks: readonly ChildTicketChunk[],
  chunkId: string,
  title: string,
): ChildTicketChunk[] {
  const trimmed = title.trim();
  if (trimmed === '') return [...chunks];
  return chunks.map((chunk) => (chunk.chunkId === chunkId ? { ...chunk, title: trimmed } : chunk));
}

/**
 * チャンクの中身（子票行）を差し替える。
 * @graph-connects none
 */
export function writeChildTicketChunkRows(
  chunks: readonly ChildTicketChunk[],
  chunkId: string,
  rows: ChildTicketRow[],
): ChildTicketChunk[] {
  return chunks.map((chunk) => (chunk.chunkId === chunkId ? { ...chunk, rows } : chunk));
}

/**
 * チャンクを並べ替える（`from` 番目を `to` 番目へ）。範囲の外は何もしない。
 * @graph-connects none
 */
export function moveChildTicketChunk(
  chunks: readonly ChildTicketChunk[],
  from: number,
  to: number,
): ChildTicketChunk[] {
  const next = [...chunks];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length || from === to) return next;
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...chunks];
  next.splice(to, 0, moved);
  return next;
}

/**
 * チャンクを 1 つ引く。知らない id では undefined を返す。
 * @graph-connects none
 */
export function findChildTicketChunk(
  chunks: readonly ChildTicketChunk[],
  chunkId: string | null,
): ChildTicketChunk | undefined {
  if (chunkId === null) return undefined;
  return chunks.find((chunk) => chunk.chunkId === chunkId);
}
